import { action, observable } from 'mobx'
import { ProjectDTO } from 'models'
import { GENERAL_STORE } from 'store/GeneralStore'
import { RootStore } from 'store/RootStore'
import { Api, applyProjectQuery, parseQueryParams } from 'utilities'
import { ProjectControllerQueryParams } from './ProjectMainStore.types'

export const PROJECT_MAIN_STORE = 'PROJECT_MAIN_STORE'

export class ProjectMainStore {
  private rootStore: RootStore

  constructor (rootStore: RootStore) {
    this.rootStore = rootStore
  }

  @observable private projectsMap: Map<number, ProjectDTO> = new Map()
  @observable projectsHierarchyList: ProjectDTO[] = []

  @action private setProjectsMap = (projectsMap: Map<number, ProjectDTO>) => {
    this.projectsMap = projectsMap
  }

  @action private setProject = (project: ProjectDTO) => {
    this.projectsMap.set(project.id, project)
  }

  @action private setProjectHierarchyList = (projectsHierarchyList: ProjectDTO[]) => {
    this.projectsHierarchyList = projectsHierarchyList
  }

  clearProjectHierarchyList = () => {
    this.setProjectHierarchyList([])
  }

  clearProjects = () => {
    this.projectsMap.clear()
  }

  getProjectsByIds = async (projectIds: number[], queryParams: {
    loadContents?: boolean
    loadHierarchy?: boolean
    parentId?: number
    showOnlyPublished?: boolean
  } = {}): Promise<ProjectDTO[]> => {
    let projects: ProjectDTO[] = []
    let filteredProjectIds: number[] = []

    filteredProjectIds = projectIds.filter(id => {
      const project = this.getProjectById(id)
      const shouldBeLoadedFromApi = !(
        project &&
        (project.loadContents || !queryParams.loadContents) &&
        (project.loadHierarchy || !queryParams.loadHierarchy)
      )

      !shouldBeLoadedFromApi && projects.push(project)
      return shouldBeLoadedFromApi
    })

    if (filteredProjectIds.length) {
      const {
        setLoadingOn,
        setLoadingOff,
        handleApiError
      } = this.rootStore[GENERAL_STORE]

      setLoadingOn()
      await Api
        .post<ProjectDTO[], { ids: number[] }>(
          `/project/all?${parseQueryParams(queryParams)}`,
          { ids: filteredProjectIds }
        )
        .then(data => {
          const setProject = (p: ProjectDTO) => {
            this.setProject(p)
            if (p.children?.length) {
              p.children.forEach(setProject)
            }
          }
          const newlyLoadedProjects = data.map(p => {
            const project = applyProjectQuery(p, queryParams)
            setProject(project)
            return project
          })

          projects = [
            ...projects,
            ...newlyLoadedProjects
          ]

          if (queryParams.loadHierarchy) {
            this.setProjectHierarchyList([
              ...this.projectsHierarchyList
                .filter(project => !projects.some(p => p.id === project.id)),
              ...projects
            ])
          }
          setLoadingOff()
        })
        .catch(handleApiError)
    }

    return projects
  }

  getProjects = async (
    queryParams: ProjectControllerQueryParams = {}
  ): Promise<ProjectDTO[]> => {
    const {
      setLoadingOn,
      setLoadingOff,
      handleApiError
    } = this.rootStore[GENERAL_STORE]

    let projects: ProjectDTO[]

    setLoadingOn()
    await Api
      .get<ProjectDTO[]>(`/project/all?${parseQueryParams(queryParams)}`)
      .then(data => {
        projects = data.map(project => applyProjectQuery(project, queryParams))

        const projectsMap = new Map<number, ProjectDTO>()
        const mapProjects = (projectsList: ProjectDTO[]) => {
          if (!projectsList || !projectsList.length) return
          projectsList.forEach(project => {
            projectsMap.set(project.id, project)
            mapProjects(project.children)
          })
        }
        mapProjects(projects)

        this.setProjectsMap(projectsMap)
        if (queryParams.loadHierarchy) this.setProjectHierarchyList(projects)
        setLoadingOff()
      })
      .catch(handleApiError)
    return projects
  }

  getProject = async (
    projectId: number,
    queryParams: ProjectControllerQueryParams = {}
  ): Promise<ProjectDTO> => {
    let project = this.projectsMap.get(projectId)

    const shouldLoadProjectFromApi = !(
      project &&
      (project.loadContents || !queryParams.loadContents) &&
      (project.loadHierarchy || !queryParams.loadHierarchy)
    )

    if (shouldLoadProjectFromApi) {
      const {
        setLoadingOn,
        setLoadingOff,
        handleApiError
      } = this.rootStore[GENERAL_STORE]

      setLoadingOn()
      await Api
        .get<ProjectDTO>(
          `/project/${projectId}/view?${parseQueryParams(queryParams)}`
        )
        .then(data => {
          const setProject = (prj: ProjectDTO) => {
            this.setProject(prj)
            if (prj.children && prj.children.length) {
              prj.children.forEach(p => setProject(applyProjectQuery(p, queryParams)))
            }
          }
          project = applyProjectQuery(data, queryParams)
          setProject(project)
          setLoadingOff()
        })
        .catch(handleApiError)
    }

    return project
  }

  getProjectById = (projectId: number): ProjectDTO => {
    return this.projectsMap.get(projectId)
  }

  doesProjectHasChildren = (projectId: number): boolean => {
    return Boolean(this.getProjectById(projectId)?.children?.length)
  }

  getProjectChildrenIds = (projectId: number): Set<number> => {
    const childrenIds: Set<number> = new Set()

    if (this.doesProjectHasChildren(projectId)) {
      const processChildren = (children: ProjectDTO[]): void => {
        children.forEach(p => {
          const hasChildren = Boolean(p.children?.length)
          if (hasChildren) {
            processChildren(p.children)
          } else {
            childrenIds.add(p.id)
          }
        })
      }
      processChildren(this.getProjectById(projectId).children)
    }

    return childrenIds
  }

  getProjectParentId = (projectId: number): number => {
    let parentId: number = null
    let newParentId = this.getProjectById(projectId)?.parentId
    while (newParentId) {
      parentId = newParentId
      newParentId = this.getProjectById(newParentId)?.parentId
    }

    return parentId
  }
}
